import type { UserEmoji } from "../model";
import { EMOJI_ITEMS } from "./emojiItems";

const POPULAR_EMOJIS_KEY = "popularEmojis";

/**
 * Проверяет валидность эмодзи
 */
const isValidUserEmoji = (item: object): item is UserEmoji => {
    if ("emojiId" in item && typeof item.emojiId === "string") {
        return item.emojiId in EMOJI_ITEMS;
    }

    return false;
};

/**
 * Получает популярные эмодзи из localStorage
 */
export const getPopularEmojis = (): UserEmoji[] => {
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            const stored = localStorage.getItem(POPULAR_EMOJIS_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    // Фильтруем невалидные эмодзи
                    const validEmojis = parsed.filter(isValidUserEmoji);
                    // Сортируем по убыванию count и ограничиваем до 16
                    const sorted = validEmojis.sort((a, b) => b.count - a.count);
                    const limited = sorted.slice(0, 16);

                    // Если были отфильтрованы невалидные эмодзи, сохраняем очищенный список
                    if (validEmojis.length !== parsed.length) {
                        savePopularEmojis(limited);
                    }

                    return limited;
                }
            }
        }
    } catch (error) {
        console.warn("Ошибка получения эмодзи из localStorage:", error);
    }

    return [];
};

/**
 * Сохраняет популярные эмодзи в localStorage
 */
export const savePopularEmojis = (emojis: UserEmoji[]): void => {
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            localStorage.setItem(POPULAR_EMOJIS_KEY, JSON.stringify(emojis));
        }
    } catch (error) {
        console.warn("Ошибка сохранения эмодзи в localStorage:", error);
    }
};

/**
 * Добавляет эмодзи к популярным или увеличивает счетчик
 */
export const addToPopularEmojis = (emojiId: string, emoji: string): void => {
    const popularEmojis = getPopularEmojis();
    const existingIndex = popularEmojis.findIndex((item) => item.emojiId === emojiId);

    if (existingIndex >= 0) {
        // Увеличиваем счетчик существующего эмодзи
        popularEmojis[existingIndex].count += 1;
        const updated = popularEmojis[existingIndex];

        // Удаляем элемент из старой позиции
        popularEmojis.splice(existingIndex, 1);

        // Находим новую позицию для вставки (список отсортирован по убыванию count)
        const newIndex = binarySearchInsertIndex(popularEmojis, updated.count);
        popularEmojis.splice(newIndex, 0, updated);
    } else {
        // Если выбранного эмодзи нет в рейтинге, всегда добавляем его
        // Если список полон, удаляем последний элемент (с наименьшим count)
        if (popularEmojis.length >= 16) {
            popularEmojis.pop();
        }

        const newEmoji = {
            emojiId,
            emoji,
            count: 1,
        };

        if (popularEmojis.length === 0 || popularEmojis[popularEmojis.length - 1].count === 1) {
            // Если список пуст или все элементы имеют count=1, добавляем в конец
            popularEmojis.push(newEmoji);
        } else {
            // Находим позицию для вставки (для новых элементов с count=1)
            const insertIndex = binarySearchInsertIndex(popularEmojis, 1);
            popularEmojis.splice(insertIndex, 0, newEmoji);
        }
    }

    savePopularEmojis(popularEmojis);
};

/**
 * Бинарный поиск индекса для вставки элемента с учетом сортировки по убыванию
 */
function binarySearchInsertIndex(arr: UserEmoji[], targetCount: number): number {
    let left = 0;
    let right = arr.length;

    while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (arr[mid].count > targetCount) {
            left = mid + 1;
        } else {
            right = mid;
        }
    }

    return left;
}
